export type User = { id: string; email: string };

export type Broker = {
  id: string;
  name: string;
  website?: string;
  opt_out_url?: string;
  privacy_request_email?: string;
};

export type Finding = {
  id: string;
  broker_id: string;
  source: string;
  source_type: string;
  title: string;
  url?: string;
  matched_value?: string;
  data_found: string;
  status: string;
  risk_level: string;
  recommended_action?: string;
  created_at?: string;
};

export type RemovalRequest = {
  id: string;
  broker_id: string;
  broker_name?: string;
  finding_id?: string;
  request_type: string;
  submission_method: string;
  status: string;
  recipient_email?: string;
  subject?: string;
  generated_message?: string;
  source_url?: string;
  sent_at?: string;
  expected_response_at?: string;
  follow_up_at?: string;
  error_message?: string;
};

function humanError(body: string, status: number) {
  const text = (body || '').trim();
  if (!text) return `Request failed with status ${status}.`;
  if (/^</.test(text) || /<!doctype|<html|<script|<div/i.test(text)) {
    return `The scanner received an HTML error page instead of JSON. This is usually a blocked/free search fetch or a Cloudflare function error. The raw page was hidden so it does not break the UI.`;
  }
  try {
    const parsed = JSON.parse(text);
    return parsed?.message || parsed?.error || JSON.stringify(parsed).slice(0, 360);
  } catch {
    return text.slice(0, 420);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {})
    }
  });

  const text = await res.text().catch(() => '');

  if (!res.ok) {
    throw new Error(humanError(text, res.status));
  }

  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('The API returned non-JSON content. The response was hidden so it does not break the page. Try Refresh, then run the scan again.');
  }
}

export async function getMe(): Promise<User | null> {
  try {
    return await api<User>('/api/auth/me');
  } catch {
    return null;
  }
}

export function statusTone(status?: string): string {
  const value = (status || '').toLowerCase();
  if (value.includes('removed') || value.includes('complete') || value.includes('suppressed') || value === 'sent' || value.includes('verified')) return 'good';
  if (value.includes('waiting') || value.includes('queued') || value.includes('scheduled') || value.includes('sending') || value.includes('possible')) return 'blue';
  if (value.includes('manual') || value.includes('review') || value.includes('draft') || value.includes('approval') || value.includes('probe')) return 'warn';
  if (value.includes('failed') || value.includes('blocked') || value.includes('reappeared') || value.includes('required') || value.includes('error')) return 'danger';
  return 'neutral';
}

export function riskTone(risk?: string): string {
  const value = (risk || '').toLowerCase();
  if (value === 'critical' || value === 'high') return 'danger';
  if (value === 'medium') return 'warn';
  if (value === 'low') return 'good';
  return 'neutral';
}

export function formatDate(value?: string) {
  if (!value) return 'Not yet';
  return new Date(value).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
